import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { CustomDatepickerHeaderComponent } from './custom-datepicker-header.component';
import { MonthPickerDOS } from './month-picker-dos.service';
import { TimesheetDOS } from '@app/timesheet-user/services/timesheet-dos.service';
import { AbstractSubscriptionManager } from '@app/shared/classes/subscription-manager';
import { ActionName } from './month-picker.constants';
import { ParentComponent } from '@app/shared/constants/parent-component';
import { MonthlyReportDOS } from '@app/monthly-report/services/monthly-report-dos.service';
import { AuthDOS } from '@app/auth/services/auth-dos.service';
import { ModalService } from '@app/shared/services/modal.service';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { MonthPickerService } from './month-picker.service';

@Component({
  selector: 'app-month-picker',
  templateUrl: './month-picker.component.html',
  styleUrls: ['./month-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonthPickerComponent extends AbstractSubscriptionManager implements OnInit {
  @Input() parentComponent: ParentComponent;
  customDatepickerHeader = CustomDatepickerHeaderComponent;
  readonly timesheetDate$: Observable<Date> = this._timesheetDOS.timesheetDate$;
  readonly monthlyReportDate$: Observable<Date> = this._monthlyReportDOS.monthlyReportDate$;
  readonly panelClass$ = this._monthPickerDOS.panelClass$;
  readonly selectedReleaseMonth$ = this._monthPickerDOS.selectedReleaseMonth$;

  currentSetMonth: Date;
  isForTimesheet: boolean;
  releaseDate: Date;

  get isDataModified(): boolean {
    if (this.isForTimesheet) {
      return this._timesheetDOS.getUserTimesheetChangeStatusSnapshot();
    }
    return false;
  }

  constructor(
    private _monthPickerDOS: MonthPickerDOS,
    private _timesheetDOS: TimesheetDOS,
    private _monthlyReportDOS: MonthlyReportDOS,
    private _authDOS: AuthDOS,
    private _modalService: ModalService,
    private _matDialog: MatDialog,
    private _monthPickerService: MonthPickerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.isForTimesheet = this.parentComponent === ParentComponent.Timesheet;
    this.releaseDate = this._authDOS.getReleaseDateSnapshot();

    if (this.isForTimesheet) {
      this.subscription.add(
        this.timesheetDate$.subscribe((selectedDate) => {
          this.currentSetMonth = selectedDate;
          this._monthPickerDOS.changeSelectedReleaseMonth(
            this._monthPickerService.areSelectedAndReleaseDateEqual(selectedDate)
          );
        })
      );
    } else {
      this.subscription.add(
        this.monthlyReportDate$.subscribe((selectedDate) => {
          this.currentSetMonth = selectedDate;
          this._monthPickerDOS.changeSelectedReleaseMonth(
            this._monthPickerService.areSelectedAndReleaseDateEqual(selectedDate)
          );
        })
      );
    }
  }

  onMonthDecrement(): void {
    this._handleMonthChange(ActionName.Decrement);
  }

  onMonthIncrement(): void {
    this._handleMonthChange(ActionName.Increment);
  }

  onMonthChange(date: Date, picker: MatDatepicker<Date>): void {
    this._handleMonthChange(ActionName.MonthChange, date);
    picker.close();
    picker.ngOnDestroy();
  }

  selectCurrentMonth(): void {
    this._handleMonthChange(ActionName.MonthChange, new Date());
  }

  isCurrentMonthSelected(): boolean {
    return this.currentSetMonth.getMonth() === new Date().getMonth();
  }

  private _handleMonthChange(monthChangeType: string, selectedDate?: Date): void {
    if (this.isDataModified) {
      this._openModal(this.parentComponent, this.currentSetMonth, monthChangeType, selectedDate);
    } else {
      this._monthPickerService.setNewMonth(this.parentComponent, this.currentSetMonth, monthChangeType, selectedDate);
    }
  }

  private _openModal(parentComponent: string, currentMonth: Date, actionType: string, selectedDate: Date): void {
    const modalConfig: MatDialogConfig = this._modalService.createUnsavedTimesheetData(
      parentComponent,
      currentMonth,
      selectedDate,
      actionType
    );
    this._matDialog.open(ConfirmModalComponent, modalConfig);
  }
}
